import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  WizardContainer,
  WizardStep,
  AIInlineSuggestion,
  AIAutoFillButton,
  StepSummaryCard,
} from "../components/WizardComponents";
import {
  FileWarning,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Target,
  Search,
  RefreshCw,
  Edit,
  Eye,
  AlertCircle,
  Shield,
  FileText,
  Sparkles,
  TrendingDown,
  Activity,
} from "lucide-react";
import AIRecommendationsButton from "../components/AIRecommendationsButton";

const PACA_TYPES = [
  { id: "preventive", name: "Preventive Action", description: "Prevent potential non-conformity", color: "bg-amber-100 text-amber-700" },
  { id: "corrective", name: "Corrective Action", description: "Fix existing non-conformity", color: "bg-red-100 text-red-700" },
];

const SEVERITY_OPTIONS = [
  { id: "critical", name: "Critical", color: "bg-red-100 text-red-700", description: "Major impact on BCM effectiveness" },
  { id: "major", name: "Major", color: "bg-sky-100 text-sky-700", description: "Significant deviation from requirements" },
  { id: "minor", name: "Minor", color: "bg-amber-100 text-amber-700", description: "Small deviation, low impact" },
  { id: "observation", name: "Observation", color: "bg-blue-100 text-blue-700", description: "Opportunity for improvement" },
];

const STATUS_OPTIONS = [
  { id: "open", name: "Open", color: "bg-red-100 text-red-700" },
  { id: "root_cause", name: "Root Cause Analysis", color: "bg-amber-100 text-amber-700" },
  { id: "action_planned", name: "Action Planned", color: "bg-blue-100 text-blue-700" },
  { id: "in_progress", name: "In Progress", color: "bg-sky-100 text-sky-700" },
  { id: "verification", name: "Pending Verification", color: "bg-purple-100 text-purple-700" },
  { id: "closed", name: "Closed", color: "bg-emerald-100 text-emerald-700" },
];

const WIZARD_STEPS = [
  { id: 1, title: "Non-Conformity", description: "Describe the issue", icon: AlertCircle },
  { id: 2, title: "Root Cause Analysis", description: "Find the cause", icon: Search },
  { id: 3, title: "Action Plan", description: "Define solution", icon: FileText },
  { id: 4, title: "Implementation", description: "Execute actions", icon: Target },
  { id: 5, title: "Effectiveness Verification", description: "Verify resolution", icon: CheckCircle2 },
  { id: 6, title: "Review & Close", description: "Submit PACA", icon: Shield },
];

export default function CAPAPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pacas, setPacas] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedPaca, setSelectedPaca] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    paca_type: "preventive",
    title: "",
    description: "",
    severity: "major",
    source: "",
    date_identified: "",
    root_cause: "",
    root_cause_method: "5_whys",
    contributing_factors: "",
    corrective_action: "",
    preventive_action: "",
    responsible_person: "",
    target_date: "",
    implementation_notes: "",
    implementation_date: "",
    verification_method: "",
    verification_date: "",
    effectiveness_criteria: "",
    effectiveness_verified: false,
    effectiveness_evidence: "",
    effectiveness_verified_by: "",
    effectiveness_verified_date: "",
    recurrence_check_date: "",
    status: "open",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/paca/");
      setPacas(response.data || []);
    } catch (error) {
      console.error("Failed to fetch PACAs:", error);
      toast.error("Failed to load PACA items");
      setPacas([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAIRootCause = async () => {
    setAiLoading(true);
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        root_cause: "Primary root cause: Lack of defined ownership for BIA process in Finance department.\n\nSecondary causes:\n1. No clear escalation path for incomplete assessments\n2. Insufficient training on BIA requirements\n3. Competing priorities without BCM accountability",
        contributing_factors: "• Resource constraints during fiscal year-end\n• Staff turnover in key positions\n• Unclear communication of BIA deadlines",
      }));
      setAiLoading(false);
      toast.success("AI root cause analysis generated!");
    }, 2000);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post("/paca/", formData);
      setPacas([...pacas, response.data]);
      toast.success("PACA created successfully!");
      setShowWizard(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to create PACA");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      paca_type: "preventive",
      title: "",
      description: "",
      severity: "major",
      source: "",
      date_identified: "",
      root_cause: "",
      root_cause_method: "5_whys",
      contributing_factors: "",
      corrective_action: "",
      preventive_action: "",
      responsible_person: "",
      target_date: "",
      verification_method: "",
      verification_date: "",
      effectiveness_criteria: "",
      status: "open",
    });
    setCurrentStep(1);
  };

  const getStatusInfo = (statusId) => STATUS_OPTIONS.find(s => s.id === statusId) || STATUS_OPTIONS[0];
  const getSeverityInfo = (severityId) => SEVERITY_OPTIONS.find(s => s.id === severityId) || SEVERITY_OPTIONS[1];
  const getTypeInfo = (typeId) => PACA_TYPES.find(t => t.id === typeId) || PACA_TYPES[0];

  // Stats
  const stats = {
    total: pacas.length,
    open: pacas.filter(c => c.status === "open" || c.status === "root_cause").length,
    inProgress: pacas.filter(c => ["action_planned", "in_progress", "verification"].includes(c.status)).length,
    closed: pacas.filter(c => c.status === "closed").length,
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="paca-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">ACT Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 10.1 / 10.2</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Corrective & Preventive Actions</h1>
          <p className="text-slate-600 mt-1">
            Unified register for corrective, preventive, and continual-improvement actions (ISO 22301 §10.1 + §10.2).{" "}
            <a href="/improvement-actions" className="text-purple-700 hover:underline font-medium" data-testid="improvement-actions-link">
              View improvement-only view →
            </a>
          </p>
        </div>
        <div className="flex gap-2">
          <AIRecommendationsButton 
            module="paca" 
            contextData={{
              total_pacas: stats.total,
              open_pacas: stats.open,
              in_progress: stats.inProgress,
              closed: stats.closed
            }}
            api={api}
          />
          <Button 
            onClick={() => setShowWizard(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="new-paca-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New PACA
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileWarning className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total PACAs</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Open</p>
                <p className="text-2xl font-bold text-slate-900">{stats.open}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">In Progress</p>
                <p className="text-2xl font-bold text-slate-900">{stats.inProgress}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Closed</p>
                <p className="text-2xl font-bold text-slate-900">{stats.closed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PACAs Table */}
      <Card>
        <CardHeader>
          <CardTitle>PACA Register</CardTitle>
          <CardDescription>Track preventive and corrective actions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : pacas.length === 0 ? (
            <div className="text-center py-12">
              <FileWarning className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No PACAs recorded yet</p>
              <Button className="mt-4" onClick={() => setShowWizard(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First PACA
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PACA ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Responsible</TableHead>
                  <TableHead>Target Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pacas.map((paca, index) => {
                  const statusInfo = getStatusInfo(paca.status);
                  const severityInfo = getSeverityInfo(paca.severity);
                  const typeInfo = getTypeInfo(paca.paca_type);
                  
                  return (
                    <TableRow key={paca.id}>
                      <TableCell className="font-mono text-sm">PACA-{String(index + 1).padStart(3, '0')}</TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>{typeInfo.name}</Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">{paca.title}</TableCell>
                      <TableCell>
                        <Badge className={severityInfo.color}>{severityInfo.name}</Badge>
                      </TableCell>
                      <TableCell>{paca.responsible_person || "-"}</TableCell>
                      <TableCell>{paca.target_date || "-"}</TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>{statusInfo.name}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedPaca(paca)} data-testid={`view-paca-${paca.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PACA Detail Dialog */}
      <Dialog open={!!selectedPaca} onOpenChange={() => setSelectedPaca(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedPaca && (
            <div className="space-y-5" data-testid="paca-detail-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">{selectedPaca.title}</DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={getTypeInfo(selectedPaca.paca_type)?.color}>{getTypeInfo(selectedPaca.paca_type)?.name}</Badge>
                  <Badge className={getSeverityInfo(selectedPaca.severity)?.color}>{getSeverityInfo(selectedPaca.severity)?.name}</Badge>
                  <Badge className={getStatusInfo(selectedPaca.status)?.color}>{getStatusInfo(selectedPaca.status)?.name}</Badge>
                </div>
              </DialogHeader>

              {selectedPaca.description && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Description</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedPaca.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Responsible Person</span>
                  <p className="font-medium">{selectedPaca.responsible_person || "Not assigned"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Target Date</span>
                  <p className="font-medium">{selectedPaca.target_date || "Not set"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date Identified</span>
                  <p className="font-medium">{selectedPaca.date_identified || "Not recorded"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Source</span>
                  <p className="font-medium">{selectedPaca.source || "Not specified"}</p>
                </div>
              </div>

              {selectedPaca.root_cause && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                    <Search className="w-4 h-4 text-purple-600" /> Root Cause Analysis
                  </h3>
                  <p className="text-sm text-slate-600 bg-purple-50 p-3 rounded-lg whitespace-pre-wrap">{selectedPaca.root_cause}</p>
                  {selectedPaca.root_cause_method && (
                    <Badge variant="outline" className="mt-2">{selectedPaca.root_cause_method.replace('_', ' ').toUpperCase()}</Badge>
                  )}
                </div>
              )}

              {selectedPaca.contributing_factors && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Contributing Factors</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedPaca.contributing_factors}</p>
                </div>
              )}

              {(selectedPaca.corrective_action || selectedPaca.preventive_action) && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Target className="w-4 h-4 text-sky-600" /> Action Plan
                  </h3>
                  {selectedPaca.corrective_action && (
                    <div>
                      <span className="text-xs font-medium text-red-600 uppercase">Corrective Action</span>
                      <p className="text-sm text-slate-600 bg-red-50 p-3 rounded-lg whitespace-pre-wrap">{selectedPaca.corrective_action}</p>
                    </div>
                  )}
                  {selectedPaca.preventive_action && (
                    <div>
                      <span className="text-xs font-medium text-amber-600 uppercase">Preventive Action</span>
                      <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-lg whitespace-pre-wrap">{selectedPaca.preventive_action}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedPaca.effectiveness_verified && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-emerald-800 flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4" /> Effectiveness Verified
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Verified By</span>
                      <p className="font-medium">{selectedPaca.effectiveness_verified_by || "-"}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Verification Date</span>
                      <p className="font-medium">{selectedPaca.effectiveness_verified_date || "-"}</p>
                    </div>
                  </div>
                  {selectedPaca.effectiveness_evidence && (
                    <div className="mt-2">
                      <span className="text-slate-500 text-sm">Evidence</span>
                      <p className="text-sm text-emerald-700">{selectedPaca.effectiveness_evidence}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-3 border-t">
                <Button variant="outline" onClick={() => setSelectedPaca(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wizard Dialog */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <WizardContainer
            title="New PACA"
            description="Document preventive or corrective action"
            badge="ISO 22301 Clause 10.1"
            steps={WIZARD_STEPS}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
            onComplete={handleSubmit}
            isLoading={submitting}
            showSaveDraft={false}
          >
            {/* Step 1: Non-Conformity */}
            <WizardStep step={1} currentStep={currentStep}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>PACA Type *</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {PACA_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, paca_type: type.id })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.paca_type === type.id
                            ? `${type.color} border-current`
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <span className="font-semibold block">{type.name}</span>
                        <span className="text-sm text-slate-500">{type.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief description of the non-conformity"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the non-conformity..."
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Severity *</Label>
                    <Select
                      value={formData.severity}
                      onValueChange={(value) => setFormData({ ...formData, severity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITY_OPTIONS.map((severity) => (
                          <SelectItem key={severity.id} value={severity.id}>{severity.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Identified</Label>
                    <Input
                      type="date"
                      value={formData.date_identified}
                      onChange={(e) => setFormData({ ...formData, date_identified: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </WizardStep>

            {/* Step 2: Root Cause Analysis */}
            <WizardStep step={2} currentStep={currentStep}>
              <div className="space-y-6">
                <AIInlineSuggestion
                  type="tip"
                  suggestion="Use the 5 Whys technique: Ask 'why' five times to drill down to the root cause. AI can help generate initial analysis."
                />
                
                <div className="flex justify-end">
                  <AIAutoFillButton
                    onAutoFill={handleAIRootCause}
                    isLoading={aiLoading}
                    label="Generate Root Cause Analysis"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Root Cause Analysis Method</Label>
                  <Select
                    value={formData.root_cause_method}
                    onValueChange={(value) => setFormData({ ...formData, root_cause_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5_whys">5 Whys</SelectItem>
                      <SelectItem value="fishbone">Fishbone Diagram</SelectItem>
                      <SelectItem value="fault_tree">Fault Tree Analysis</SelectItem>
                      <SelectItem value="pareto">Pareto Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Root Cause</Label>
                  <Textarea
                    value={formData.root_cause}
                    onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                    placeholder="Describe the root cause(s) identified..."
                    rows={5}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Contributing Factors</Label>
                  <Textarea
                    value={formData.contributing_factors}
                    onChange={(e) => setFormData({ ...formData, contributing_factors: e.target.value })}
                    placeholder="List contributing factors..."
                    rows={3}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 3: Action Plan */}
            <WizardStep step={3} currentStep={currentStep}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Corrective Action</Label>
                  <Textarea
                    value={formData.corrective_action}
                    onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
                    placeholder="Actions to correct the non-conformity..."
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Preventive Action</Label>
                  <Textarea
                    value={formData.preventive_action}
                    onChange={(e) => setFormData({ ...formData, preventive_action: e.target.value })}
                    placeholder="Actions to prevent recurrence..."
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Responsible Person</Label>
                    <Input
                      value={formData.responsible_person}
                      onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                      placeholder="Person responsible for actions"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Completion Date</Label>
                    <Input
                      type="date"
                      value={formData.target_date}
                      onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </WizardStep>

            {/* Step 4: Implementation */}
            <WizardStep step={4} currentStep={currentStep}>
              <div className="space-y-6">
                <AIInlineSuggestion
                  type="info"
                  suggestion="Document the implementation of corrective/preventive actions. Include dates, resources used, and any challenges encountered."
                />
                
                <div className="space-y-2">
                  <Label>Implementation Notes</Label>
                  <Textarea
                    value={formData.implementation_notes}
                    onChange={(e) => setFormData({ ...formData, implementation_notes: e.target.value })}
                    placeholder="Describe how the action was implemented..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Implementation Date</Label>
                  <Input
                    type="date"
                    value={formData.implementation_date}
                    onChange={(e) => setFormData({ ...formData, implementation_date: e.target.value })}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 5: Effectiveness Verification - ISO 10.1 */}
            <WizardStep step={5} currentStep={currentStep}>
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-800">Effectiveness Verification</span>
                    <Badge variant="outline" className="text-xs">ISO 10.1</Badge>
                  </div>
                  <p className="text-sm text-purple-700">
                    Verify that the corrective action has eliminated the root cause and the non-conformity will not recur.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Verification Method</Label>
                  <Textarea
                    value={formData.verification_method}
                    onChange={(e) => setFormData({ ...formData, verification_method: e.target.value })}
                    placeholder="How will you verify the action was effective?"
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Effectiveness Criteria</Label>
                  <Textarea
                    value={formData.effectiveness_criteria}
                    onChange={(e) => setFormData({ ...formData, effectiveness_criteria: e.target.value })}
                    placeholder="What criteria will demonstrate effectiveness?"
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Verified By</Label>
                    <Input
                      value={formData.effectiveness_verified_by}
                      onChange={(e) => setFormData({ ...formData, effectiveness_verified_by: e.target.value })}
                      placeholder="Name of verifier"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Verification Date</Label>
                    <Input
                      type="date"
                      value={formData.effectiveness_verified_date}
                      onChange={(e) => setFormData({ ...formData, effectiveness_verified_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Evidence of Effectiveness</Label>
                  <Textarea
                    value={formData.effectiveness_evidence}
                    onChange={(e) => setFormData({ ...formData, effectiveness_evidence: e.target.value })}
                    placeholder="Document the evidence that proves the action was effective..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Recurrence Check Date (30-90 days)</Label>
                  <Input
                    type="date"
                    value={formData.recurrence_check_date}
                    onChange={(e) => setFormData({ ...formData, recurrence_check_date: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">Schedule a follow-up check to confirm non-recurrence</p>
                </div>
                
                <div className="flex items-center gap-3 p-4 border-2 rounded-lg border-emerald-300 bg-emerald-50">
                  <input
                    type="checkbox"
                    id="effectiveness_verified"
                    checked={formData.effectiveness_verified}
                    onChange={(e) => setFormData({ ...formData, effectiveness_verified: e.target.checked })}
                    className="w-5 h-5 rounded border-emerald-400"
                  />
                  <label htmlFor="effectiveness_verified" className="text-emerald-800 font-medium">
                    I confirm that the corrective action has been verified as effective
                  </label>
                </div>
              </div>
            </WizardStep>

            {/* Step 6: Review & Close */}
            <WizardStep step={6} currentStep={currentStep}>
              <div className="space-y-4">
                <AIInlineSuggestion
                  type="info"
                  suggestion="This PACA will be logged in the audit trail and tracked until closure. Ensure all details are accurate."
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <StepSummaryCard
                    title="Non-Conformity"
                    icon={AlertCircle}
                    items={[
                      { label: "Type", value: getTypeInfo(formData.paca_type)?.name },
                      { label: "Severity", value: getSeverityInfo(formData.severity)?.name },
                      { label: "Title", value: formData.title },
                    ]}
                    onEdit={() => setCurrentStep(1)}
                  />
                  
                  <StepSummaryCard
                    title="Root Cause"
                    icon={Search}
                    items={[
                      { label: "Method", value: formData.root_cause_method?.replace('_', ' ').toUpperCase() },
                      { label: "Cause Identified", value: formData.root_cause ? "Yes" : "No" },
                    ]}
                    onEdit={() => setCurrentStep(2)}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <StepSummaryCard
                    title="Action Plan"
                    icon={FileText}
                    items={[
                      { label: "Responsible", value: formData.responsible_person },
                      { label: "Target Date", value: formData.target_date },
                    ]}
                    onEdit={() => setCurrentStep(3)}
                  />
                  
                  <StepSummaryCard
                    title="Effectiveness Verification"
                    icon={CheckCircle2}
                    items={[
                      { label: "Verified", value: formData.effectiveness_verified ? "Yes" : "No" },
                      { label: "Verified By", value: formData.effectiveness_verified_by || "Pending" },
                      { label: "Recurrence Check", value: formData.recurrence_check_date || "Not scheduled" },
                    ]}
                    onEdit={() => setCurrentStep(5)}
                  />
                </div>
              </div>
            </WizardStep>
          </WizardContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
}
